/**
 * The RemoteTransform pass. A RemoteTransform3D or RemoteTransform2D copies its own transform onto
 * the node its `remote_path` names on enter-tree (Godot's `_update_remote()`), so a static render
 * shows the target at the relay's transform. It is a description-level effect, not simulation, so
 * ADR-0008 (what a node draws) does not govern it. It is a live scene pass (ADR-0060), as Godot
 * resolves the path with `get_node` on the live tree (`remote_transform_3d.cpp:35-36`), across an
 * instance border too. Semantics are measured on Godot 4.6.3.
 */
import { parentPathOf, relativeTo } from '../../../utils/nodePath.js';
import { descendsFromClass } from '../../../godot/classdb/classBaseTypes.js';
import { resolveNodePathLiteral } from '../../../godot/string/nodePath.js';
import { nodesOnTheWayTo } from '../../../r3f/liveGlobalTransform.js';
import { uniquePathsOfLiteral } from '../../../r3f/nodePathUniqueNames.js';
import { globalMatrix3D, matrixToTransform3D } from '../../../r3f/nodeTreeTransforms.js';
import { node2DLocalTransform } from '../../../r3f/node2dTransform.js';
import { TRANSFORM2D_IDENTITY, affineInverseTransform2D, multiplyTransform2D, transform2DDecompose, transform2DGetScale, transform2DSetScale, } from '../../../godot/math/transform2d.js';
import { affineInverseTransform3D, identityTransform3D, multiplyTransform3D, transformColumnsAlong, } from '../../../godot/math/transform3d.js';
/**
 * The properties every RemoteTransform of `tree` pushes onto its target, by the target's live path.
 * A relay reads the transforms the relays before it, in tree order, have already pushed, as Godot's
 * enter-tree order applies a one-direction chain. A feedback loop between relays is not iterated
 * to a fixed point. A target that is not a Node3D (for a RemoteTransform3D) or a Node2D
 * (for a RemoteTransform2D) takes nothing, as the cast fails (`remote_transform_3d.cpp:54`).
 */
export function applyRemoteTransforms(tree) {
    const placed = new Map();
    tree.fold(undefined, (relay, _, descend) => {
        const { type } = relay.step.node;
        if (type === 'RemoteTransform3D')
            placeTarget3D(relay, tree, placed);
        else if (type === 'RemoteTransform2D')
            placeTarget2D(relay, tree, placed);
        descend();
    });
    return placed;
}
/**
 * Whether the live paths put one node on the other's line of the tree: the same node, or one
 * above the other. Godot refuses such a target (`remote_transform_3d.cpp:37`).
 */
function isOnOneLine(path, otherPath) {
    return relativeTo(path, otherPath) !== undefined || relativeTo(otherPath, path) !== undefined;
}
/**
 * The node `relay`'s `remote_path` names, when it descends from `targetClass` and is not the relay,
 * its ancestor or its descendant, else null.
 */
function remoteTargetOf(relay, tree, targetClass) {
    const remotePath = relay.step.node.properties.remote_path;
    const targetPath = resolveNodePathLiteral(relay.path, remotePath, uniquePathsOfLiteral(remotePath, relay.path, tree));
    const target = targetPath === null ? null : tree.at(targetPath);
    if (!target || isOnOneLine(relay.path, target.path))
        return null;
    return descendsFromClass(target.step.node.type, targetClass) ? target : null;
}
/** Parse a relay's flags, each defaulting to Godot's `true`. */
function readFlags(props) {
    return {
        updatePosition: props.update_position !== false,
        updateRotation: props.update_rotation !== false,
        updateScale: props.update_scale !== false,
        useGlobal: props.use_global_coordinates !== false,
    };
}
/** The placement of `relay` onto a `targetClass` node, or null where it drives nothing. */
function placementOf(relay, tree, placed, targetClass) {
    const target = remoteTargetOf(relay, tree, targetClass);
    if (!target)
        return null;
    const flags = readFlags(relay.step.node.properties);
    // With no part flagged, the 2D relay returns (`remote_transform_2d.cpp:55-57`) and the 3D relay
    // writes the target's own transform back, so neither moves the target.
    if (!(flags.updatePosition || flags.updateRotation || flags.updateScale))
        return null;
    return {
        target,
        flags,
        relayChain: nodesOnTheWayTo(tree, relay.path, placed),
        targetChain: nodesOnTheWayTo(tree, target.path, placed),
        parentPath: parentPathOf(target.path),
    };
}
function placeTarget3D(relay, tree, placed) {
    const placement = placementOf(relay, tree, placed, 'Node3D');
    if (!placement)
        return;
    const { target, flags, relayChain, targetChain, parentPath } = placement;
    // A 3D relay in local coordinates pushes on a change of its own local transform, which a load
    // does not make (measured on Godot 4.6.3), so the target stays where the scene puts it.
    if (!flags.useGlobal)
        return;
    const desired = composeSelected3D(globalTransform3D(target.path, targetChain), globalTransform3D(relay.path, relayChain), flags);
    const parentGlobal = parentPath ? globalTransform3D(parentPath, targetChain) : identityTransform3D();
    placed.set(target.path, {
        transform: multiplyTransform3D(affineInverseTransform3D(parentGlobal), desired),
    });
}
function globalTransform3D(path, nodeByPath) {
    return matrixToTransform3D(globalMatrix3D(path, nodeByPath));
}
/**
 * The global transform `target` takes from `relay` (`_update_remote`, `remote_transform_3d.cpp:63-99`).
 * The flagged parts come from the relay: the whole basis for rotation and scale, the direction of
 * each column for rotation, the length of each column for scale, the origin for position. The basis
 * is never decomposed, so a shear and a mirror stay as they are.
 */
function composeSelected3D(target, relay, flags) {
    const { updatePosition, updateRotation, updateScale } = flags;
    if (updatePosition && updateRotation && updateScale)
        return relay;
    const origin = updatePosition ? relay.origin : target.origin;
    if (updateRotation && updateScale)
        return { ...relay, origin };
    if (updateRotation)
        return { ...transformColumnsAlong(relay, target), origin };
    if (updateScale)
        return { ...transformColumnsAlong(target, relay), origin };
    return { ...target, origin };
}
/** The target's local transform when the relay and the target meet in global coordinates. */
function newLocalFromGlobals(relay, placement) {
    const { target, flags, relayChain, targetChain, parentPath } = placement;
    const desired = composeSelected2D(globalTransform2D(target.path, targetChain), globalTransform2D(relay.path, relayChain), flags);
    const parentGlobal = parentPath ? globalTransform2D(parentPath, targetChain) : TRANSFORM2D_IDENTITY;
    return multiplyTransform2D(affineInverseTransform2D(parentGlobal), desired);
}
/** The target's local transform when the relay and the target meet in local coordinates. */
function newLocalFromLocals(relay, { target, flags, relayChain, targetChain }) {
    return composeSelected2D(localTransform2D(targetChain.get(target.path)), localTransform2D(relayChain.get(relay.path)), flags);
}
function placeTarget2D(relay, tree, placed) {
    const placement = placementOf(relay, tree, placed, 'Node2D');
    if (!placement)
        return;
    const { target, flags } = placement;
    // Unlike a 3D relay, a 2D relay in local coordinates pushes on load (measured on Godot 4.6.3).
    const newLocal = flags.useGlobal
        ? newLocalFromGlobals(relay, placement)
        : newLocalFromLocals(relay, placement);
    // The parts Node2D reads from the matrix (`node_2d.cpp:127-133`), skew included.
    const { position, rotation, scale, skew } = transform2DDecompose(newLocal);
    placed.set(target.path, { position, rotation, scale, skew });
}
function localTransform2D(node) {
    const props = node.properties;
    if (!props || props.position === undefined)
        return TRANSFORM2D_IDENTITY;
    return node2DLocalTransform(props);
}
function globalTransform2D(path, nodeByPath) {
    let result = TRANSFORM2D_IDENTITY;
    let acc = '';
    for (const segment of path.split('/')) {
        acc = acc ? `${acc}/${segment}` : segment;
        const node = nodeByPath.get(acc);
        if (node)
            result = multiplyTransform2D(result, localTransform2D(node));
    }
    return result;
}
/**
 * The transform `target` takes from `relay`, in global or in local coordinates, whichever the two
 * arguments are in (`remote_transform_2d.cpp:63-107`). It starts from the relay's matrix when the rotation is flagged,
 * else from the target's. Position then differs by an exclusive-or against rotation, and scale
 * likewise: a flag that differs from the rotation flag swaps that part to the flagged side.
 */
function composeSelected2D(target, relay, flags) {
    const { updatePosition, updateRotation, updateScale } = flags;
    if (updatePosition && updateRotation && updateScale)
        return relay;
    let composed = updateRotation ? relay : target;
    if (updateRotation !== updatePosition) {
        const origin = updatePosition ? relay : target;
        composed = { ...composed, tx: origin.tx, ty: origin.ty };
    }
    if (updateRotation !== updateScale) {
        composed = transform2DSetScale(composed, transform2DGetScale(updateScale ? relay : target));
    }
    return composed;
}
//# sourceMappingURL=remoteTransforms.js.map